import { clsx } from 'clsx'
import { Base, MagnifyingGlassIcon, MenuItem } from '@itsrever/design-system'
import { useAppSelector } from '@/redux/hooks'
import { useSelectEcommerce, useSelectGroup } from '@/hooks'
import { useTranslation } from 'react-i18next'
import { type ChangeEvent, useEffect, useMemo, useState } from 'react'
import { StoreGroup, filterStoreGroups } from '@/domain/store-group'
import { useStoreGroups } from '@/hooks/use-store-groups'

const { Input } = Base

const PAGE_SIZE = 20

export const EcommerceList = () => {
  const { t } = useTranslation()
  const selectEcommerce = useSelectEcommerce()
  const selectGroupId = useSelectGroup()
  const groups = useStoreGroups()
  const ecommerceCount = useAppSelector(
    (s) => s.userApi.getMe.response.user?.e_commerces_ids?.length ?? 0
  )

  const selectedEcommerce = useAppSelector(
    (s) => s.generalData.selectedEcommerce
  )
  const selectedGroup = useAppSelector((s) => s.generalData.selectedGroup)

  const [query, setQuery] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [visibleCounts, setVisibleCounts] = useState<Record<string, number>>({})

  useEffect(() => {
    const timeoutId = setTimeout(() => setSearchTerm(query), 300)
    return () => clearTimeout(timeoutId)
  }, [query])

  const visibleGroups = useMemo(
    () => filterStoreGroups(groups, searchTerm, visibleCounts),
    [groups, searchTerm, visibleCounts]
  )

  // Clicking the selected row again clears the filter, the way back to every store without an All
  // row. A lone group already is every store, and a one-store group is that store.
  const toggleEcommerce = (ecom: string) => {
    if (selectedEcommerce !== ecom) selectEcommerce(ecom)
    // A user's only store stays picked, or every per-store page would ask for one.
    else if (ecommerceCount > 1) selectEcommerce(undefined)
  }
  const selectGroup = (group: StoreGroup) => {
    if (group.ecommerces.length === 1) toggleEcommerce(group.ecommerces[0])
    else if (groups.length === 1) selectEcommerce(undefined)
    else selectGroupId(selectedGroup === group.key ? undefined : group.key)
  }
  const isGroupSelected = (group: StoreGroup) => {
    if (group.ecommerces.length === 1)
      return selectedEcommerce === group.ecommerces[0]
    if (groups.length === 1) return !selectedEcommerce && !selectedGroup
    return selectedGroup === group.key
  }

  const showMore = (group: StoreGroup, shown: number) =>
    setVisibleCounts((prev) => ({ ...prev, [group.key]: shown + PAGE_SIZE }))

  return (
    <>
      <div className="mb-2">
        <Input
          value={query}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            setQuery(e.target.value)
          }
          type="search"
          placeholder={t('selector_component.search_placeholder')}
          aria-label={t('selector_component.search_placeholder')}
          prefix={<MagnifyingGlassIcon className="size-5" />}
          clear={query.length > 0}
          onClear={() => setQuery('')}
        />
      </div>

      <p className="px-2 pb-1 pt-2 text-xs font-medium uppercase tracking-wider text-[#99A0AE]">
        {t('selector_component.groups')}
      </p>

      {!visibleGroups.length ? (
        <p className="cursor-pointer rounded-md p-2 text-white data-[focus]:bg-[#2C3242]">
          {t('selector_component.no_results')}
        </p>
      ) : null}

      {visibleGroups.map(({ group, stores, hidden }) => (
        <div key={group.key}>
          <MenuItem>
            <p
              className={clsx(
                'flex cursor-pointer items-center justify-between gap-2 rounded-md p-2 text-sm font-semibold text-white data-[focus]:bg-[#2C3242]',
                isGroupSelected(group) && 'bg-[#2C3242]'
              )}
              aria-current={isGroupSelected(group) || undefined}
              onClick={() => selectGroup(group)}
            >
              <span className="truncate">{group.name}</span>
              <span className="shrink-0 text-xs font-light text-[#99A0AE]">
                {group.ecommerces.length}
              </span>
            </p>
          </MenuItem>

          {stores.map((ecom) => (
            <MenuItem key={ecom}>
              <p
                className={clsx(
                  'cursor-pointer truncate rounded-md py-2 pl-6 pr-2 text-sm font-light text-white data-[focus]:bg-[#2C3242]',
                  selectedEcommerce === ecom && 'bg-[#2C3242]'
                )}
                aria-current={selectedEcommerce === ecom || undefined}
                onClick={() => toggleEcommerce(ecom)}
              >
                {ecom}
              </p>
            </MenuItem>
          ))}

          {hidden > 0 ? (
            <MenuItem>
              <p
                className="cursor-pointer rounded-md py-2 pl-6 pr-2 text-sm font-light text-white data-[focus]:bg-[#2C3242]"
                data-keeps-menu-open
                onClick={(e) => {
                  // Stops Headless UI closing the menu, so the group expands in place.
                  e.preventDefault()
                  showMore(group, stores.length)
                }}
              >
                {t('selector_component.more', { count: hidden })}
              </p>
            </MenuItem>
          ) : null}
        </div>
      ))}
    </>
  )
}
